import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  type AgentInboundMessageMetadata,
  type AgentInboundMessagePill,
  type SessionArtifactWrite,
} from '@air/server-sdk';

import { usePrivateWorkspaceAgentElicitations } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentElicitations';
import { usePrivateWorkspaceAgentMessageCatchUp } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentMessageCatchUp';
import { usePrivateWorkspaceAgentMessageStore } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentMessageStore';
import { usePrivateWorkspaceAgentRunState } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentRunState';
import { usePrivateWorkspaceAgentStallMessageRefresh } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentStallMessageRefresh';
import { usePrivateWorkspaceAgentSessionCancelMutation } from '~/components/v2/components/workspace/workspace-agent-panel/queries/mutations/usePrivateWorkspaceAgentSessionCancelMutation';
import { usePrivateWorkspaceAgentSessionSendMessageMutation } from '~/components/v2/components/workspace/workspace-agent-panel/queries/mutations/usePrivateWorkspaceAgentSessionSendMessageMutation';
import { usePrivateWorkspaceAgentSessionStartMutation } from '~/components/v2/components/workspace/workspace-agent-panel/queries/mutations/usePrivateWorkspaceAgentSessionStartMutation';
import { getPrivateWorkspaceAgentSessionGetKey } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionGet';
import {
  isPrivateWorkspaceAgentSessionMessagesInfiniteKey,
  usePrivateWorkspaceAgentSessionMessagesInfinite,
} from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionMessagesInfinite';
import {
  buildPrivateWorkspaceAgentSessionsListArchivedParams,
  buildPrivateWorkspaceAgentSessionsListInfiniteParams,
  isPrivateWorkspaceAgentSessionsListInfiniteKey,
  usePrivateWorkspaceAgentSessionsListInfinite,
} from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionsListInfinite';
import {
  type WorkspaceAgentRunFinishedEvent,
  type WorkspaceAgentRunStartedEvent,
} from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentRun';
import { type WorkspaceAgentSessionArchivedEvent } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentSessionArchived';
import { type WorkspaceAgentSessionUnarchivedEvent } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentSessionUnarchived';
import { type WorkspaceAgentSessionUpdatedEvent } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentSessionUpdate';
import { buildWorkspaceAgentSessionListCachePatch } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentSessionListCachePatch';
import { shouldClearWorkspaceAgentAwaitingRunStart } from '~/components/v2/components/workspace/workspace-agent-panel/utils/hasWorkspaceAgentAssistantReplyAfterLatestUserMessage';
import { isWorkspaceAgentComposerRunLocked } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentComposerRunLocked';
import { isWorkspaceAgentSessionRunning } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentSessionRunning';
import { isWorkspaceAgentSessionUnresolvableError } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentSessionUnresolvableError';
import {
  movePrivateWorkspaceAgentSessionToArchivedListCache,
  movePrivateWorkspaceAgentSessionToLiveListCache,
  patchPrivateWorkspaceAgentSessionsListCache,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/patchPrivateWorkspaceAgentSessionsListCache';
import { refreshWorkspaceAgentOpenSession } from '~/components/v2/components/workspace/workspace-agent-panel/utils/refreshWorkspaceAgentOpenSession';
import {
  beginCancel,
  beginSend,
  cancelFailed,
  cancelSucceeded,
  createWorkspaceAgentCancelRunGuard,
  evaluateRunFinished,
  evaluateRunStarted,
  resetWorkspaceAgentCancelRunGuard,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentCancelRunGuard';
import { WORKSPACE_AGENT_CLIENT_MESSAGE_ID_KEY } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentMessageStore';
import {
  getWorkspaceAgentLastMessageActivityKey,
  hasWorkspaceAgentPendingApproval,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentStallMessageRefresh';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { useWebsocketContext } from '~/providers/SocketContext/SocketContext';
import { ReadyState } from '~/types/sockets';

const WORKSPACE_AGENT_ERRORS = {
  CANCEL_RUN: 'Could not stop the agent.',
  LOAD_CHAT: 'Could not load this chat.',
  SEND_MESSAGE: 'Could not send your message.',
  START_SESSION: 'Could not start the agent session.',
  UNAVAILABLE_CHAT: 'This chat is unavailable.',
} as const;

/** Safety valve when HTTP send succeeds but WSS never delivers run lifecycle events. */
const AWAITING_RUN_START_TIMEOUT_MS = 60_000;

const getErrorMessage = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback);

export type SendWorkspaceAgentMessageParams = {
  artifacts?: SessionArtifactWrite[];
  content: string;
  metadata: AgentInboundMessageMetadata;
  /** List membership for the optimistic bubble. The request metadata stays handles-only. */
  optimisticPills?: readonly AgentInboundMessagePill[];
};

type StartWorkspaceAgentSessionParams = SendWorkspaceAgentMessageParams & {
  clientMessageId: string;
};

export type WorkspaceAgentSendMessageResult =
  | { didSend: true; sessionId: string }
  | { didSend: false; sessionId: null };

export const failedWorkspaceAgentSendMessageResult: WorkspaceAgentSendMessageResult = {
  didSend: false,
  sessionId: null,
};

export const sentWorkspaceAgentMessageResult = (sessionId: string): WorkspaceAgentSendMessageResult => ({
  didSend: true,
  sessionId,
});

type ApplySessionIdOptions = {
  /** First-message WSS adoption — keep the send→Run.started bridge across `null` → id. */
  preserveAwaitingRunStart?: boolean;
};

export type UsePrivateWorkspaceAgentSessionParams = {
  workspaceId: string;
};

export const usePrivateWorkspaceAgentSession = ({ workspaceId }: UsePrivateWorkspaceAgentSessionParams) => {
  const { readyState } = useWebsocketContext();
  const queryClient = useQueryClient();

  const { isPending: isStarting, mutateAsync: startSessionRequest } = usePrivateWorkspaceAgentSessionStartMutation();
  const { isPending: isSendingMessage, mutateAsync: sendMessageRequest } =
    usePrivateWorkspaceAgentSessionSendMessageMutation();
  const { mutateAsync: cancelSessionRequest } = usePrivateWorkspaceAgentSessionCancelMutation();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancellingSessionId, setCancellingSessionId] = useState<string | null>(null);
  // Bridges send settling → WSS `Run.started` at session scope so it survives panel minimize (composer unmounts).
  const [isAwaitingRunStart, setIsAwaitingRunStart] = useState(false);

  const sessionIdRef = useRef<string | null>(null);
  const wasSocketOpenRef = useRef<boolean>(false);
  const cancellingSessionIdRef = useRef<string | null>(null);
  /** Invalidates in-flight cancel HTTP handlers when WSS unlocks first or a newer cancel starts. */
  const cancelGenerationRef = useRef(0);
  const isSendingRef = useRef<boolean>(false);
  const cancelRunGuardRef = useRef(createWorkspaceAgentCancelRunGuard());
  const applyUnavailableSessionRef = useRef<(error: unknown) => void>(() => undefined);

  // A ref rather than the state value, so a socket event dispatched in the same tick as a session
  // switch is judged against the session that is current when it fires, not the one that was on
  // screen when the handler was created.
  const getActiveSessionId = useCallback(() => sessionIdRef.current, []);

  const sessionsListParams = useMemo(
    () => buildPrivateWorkspaceAgentSessionsListInfiniteParams(workspaceId),
    [workspaceId],
  );
  const archivedSessionsListParams = useMemo(
    () => buildPrivateWorkspaceAgentSessionsListArchivedParams(workspaceId),
    [workspaceId],
  );

  const sessionsQuery = usePrivateWorkspaceAgentSessionsListInfinite(sessionsListParams);
  const archivedSessionsQuery = usePrivateWorkspaceAgentSessionsListInfinite(archivedSessionsListParams);
  const messagesQuery = usePrivateWorkspaceAgentSessionMessagesInfinite({
    workspaceId,
    sessionId: sessionId ?? undefined,
  });

  const { refetch: refetchLiveSessions } = sessionsQuery;
  const { refetch: refetchArchivedSessions } = archivedSessionsQuery;

  // Deliberately without `throwOnError`, which is the only way a `refetch` rejects. `start` awaits
  // this once the session already exists, so a rejection would be reported as a failed send for a
  // message the server has — and answering that with a retry would duplicate it. A selector that
  // lists the new conversation a refetch late is the cheaper failure.
  const refreshSessions = useCallback(async () => {
    await Promise.all([refetchLiveSessions(), refetchArchivedSessions()]);
  }, [refetchArchivedSessions, refetchLiveSessions]);

  const refreshOpenSession = useCallback(async () => {
    await refreshWorkspaceAgentOpenSession({
      getActiveSessionId,
      queryClient,
      workspaceId,
    });
  }, [getActiveSessionId, queryClient, workspaceId]);

  const {
    addOptimisticUserMessage,
    adoptOptimisticMessagesSession,
    getMessagesSnapshot,
    mergeServerMessages,
    messages,
    removeOptimisticUserMessage,
    upsertMessage,
  } = usePrivateWorkspaceAgentMessageStore({
    getActiveSessionId,
    serverPages: messagesQuery.data?.pages,
    sessionId,
    workspaceId,
  });

  const onSessionLoadError = useCallback((error: unknown) => {
    applyUnavailableSessionRef.current(error);
  }, []);

  const {
    appendParsedElicitation,
    clearElicitationFollowUp,
    clearElicitations,
    elicitationFollowUpAssistantMessageIds,
    elicitationFollowUpSawSessionActivity,
    getConsentRememberForSession,
    noteElicitationFollowUpSawSessionActivity,
    isAwaitingElicitationFollowUp,
    pendingElicitations,
    refreshPendingElicitations,
    removeElicitation,
    respondToElicitation,
    setConsentRememberForSession,
  } = usePrivateWorkspaceAgentElicitations({
    getActiveSessionId,
    getMessagesSnapshot,
    onError: setError,
    onSessionLoadError,
    sessionId,
    workspaceId,
  });

  const getHasPendingApproval = useCallback(
    () => hasWorkspaceAgentPendingApproval({ pendingElicitations }),
    [pendingElicitations],
  );

  const { cancelLiveSessionCatchUp, refreshLiveSessionMessages, scheduleLiveSessionCatchUp } =
    usePrivateWorkspaceAgentMessageCatchUp({
      getActiveSessionId,
      getHasPendingApproval,
      mergeServerMessages,
      workspaceId,
    });

  const liveSessions = useMemo(
    () => sessionsQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [sessionsQuery.data?.pages],
  );
  const archivedSessions = useMemo(
    () => archivedSessionsQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [archivedSessionsQuery.data?.pages],
  );
  const sessions = useMemo(() => [...liveSessions, ...archivedSessions], [archivedSessions, liveSessions]);

  const hasNextLiveSessionsPage = sessionsQuery.hasNextPage;
  const hasNextArchivedSessionsPage = archivedSessionsQuery.hasNextPage;
  const hasNextSessionsPage = hasNextLiveSessionsPage || hasNextArchivedSessionsPage;
  const isFetchingNextLiveSessionsPage = sessionsQuery.isFetchingNextPage;
  const isFetchingNextArchivedSessionsPage = archivedSessionsQuery.isFetchingNextPage;
  const isFetchingNextSessionsPage = isFetchingNextLiveSessionsPage || isFetchingNextArchivedSessionsPage;
  const fetchNextLiveSessionsPage = sessionsQuery.fetchNextPage;
  const fetchNextArchivedSessionsPage = archivedSessionsQuery.fetchNextPage;

  const hasNextMessagesPage = messagesQuery.hasNextPage;
  const isFetchingNextMessagesPage = messagesQuery.isFetchingNextPage;
  const fetchNextMessagesPage = messagesQuery.fetchNextPage;

  const openSessionStatus = useMemo(
    () => sessions.find((session) => session.id === sessionId)?.status,
    [sessionId, sessions],
  );
  const isSessionProcessing = openSessionStatus === 'processing';
  const isSessionAwaitingInput = openSessionStatus === 'inputRequested';

  const {
    activeRunId,
    getRunsById,
    handleCancelSucceeded,
    handleRunFinished,
    handleRunStarted,
    linkActiveRunToUserMessage,
    resetRunState,
    runsById,
  } = usePrivateWorkspaceAgentRunState({
    getActiveSessionId,
  });

  const isCancelling = cancellingSessionId !== null && cancellingSessionId === sessionId;
  const isSending = isSendingMessage || isStarting;
  const isComposerRunLocked = useMemo(
    () =>
      isWorkspaceAgentComposerRunLocked({
        activeRunId,
        isAwaitingRunStart,
        isCancelling,
        isSending,
      }),
    [activeRunId, isAwaitingRunStart, isCancelling, isSending],
  );
  const isLiveComposerRunLocked = useMemo(
    () =>
      isWorkspaceAgentComposerRunLocked({
        activeRunId,
        isAwaitingRunStart,
        isCancelling: false,
        isSending,
      }),
    [activeRunId, isAwaitingRunStart, isSending],
  );

  useEffect(() => {
    if (cancellingSessionId === null) {
      return;
    }

    if (cancellingSessionId !== sessionId) {
      cancelGenerationRef.current += 1;
      cancellingSessionIdRef.current = null;
      setCancellingSessionId(null);
      return;
    }

    if (!isLiveComposerRunLocked) {
      cancelGenerationRef.current += 1;
      cancellingSessionIdRef.current = null;
      setCancellingSessionId(null);
    }
  }, [cancellingSessionId, isLiveComposerRunLocked, sessionId]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    isSendingRef.current = isSending;
  }, [isSending]);

  const onRunStarted = useCallback(
    (event: WorkspaceAgentRunStartedEvent) => {
      if (evaluateRunStarted(cancelRunGuardRef.current, event).action === 'suppress') {
        return;
      }

      handleRunStarted(event);

      // Run state hook is WSS-only; bridge clear lives here so it shares session scope with `sendMessage`.
      if (event.sessionId === getActiveSessionId()) {
        setIsAwaitingRunStart(false);
      }
    },
    [getActiveSessionId, handleRunStarted],
  );

  const onRunFinished = useCallback(
    (event: WorkspaceAgentRunFinishedEvent) => {
      const { shouldClearAwaitingRunStart } = evaluateRunFinished(
        cancelRunGuardRef.current,
        event,
        getActiveSessionId(),
      );

      handleRunFinished(event);

      if (shouldClearAwaitingRunStart) {
        setIsAwaitingRunStart(false);
      }
    },
    [getActiveSessionId, handleRunFinished],
  );

  // Messages sent while the socket was down never reached us, so backfill on reconnect.
  useEffect(() => {
    const isSocketOpen = readyState === ReadyState.OPEN;
    const wasSocketOpen = wasSocketOpenRef.current;

    wasSocketOpenRef.current = isSocketOpen;

    if (!isSocketOpen || wasSocketOpen) {
      return;
    }

    void queryClient.invalidateQueries({
      predicate: ({ queryKey }) => isPrivateWorkspaceAgentSessionsListInfiniteKey(queryKey),
    });

    const reconnectedSessionId = getActiveSessionId();

    if (reconnectedSessionId === null) {
      return;
    }

    // Only the session on screen missed anything, so leave every other cached conversation alone.
    void queryClient.invalidateQueries({
      predicate: ({ queryKey }) =>
        isPrivateWorkspaceAgentSessionMessagesInfiniteKey(queryKey) && queryKey[1].sessionId === reconnectedSessionId,
    });

    // A prompt raised while the socket was down announced itself to nobody, and an unanswered one
    // blocks the agent indefinitely.
    refreshPendingElicitations();
  }, [getActiveSessionId, readyState, queryClient, refreshPendingElicitations]);

  // Drop the send→Run.started bridge once WSS sets a live run id, or catch-up already has the reply.
  useEffect(() => {
    if (!isAwaitingRunStart) {
      return;
    }

    if (shouldClearWorkspaceAgentAwaitingRunStart({ activeRunId, messages })) {
      setIsAwaitingRunStart(false);
    }
  }, [activeRunId, isAwaitingRunStart, messages]);

  // HTTP send succeeded but WSS never delivered run lifecycle — unlock so the user can retry.
  useEffect(() => {
    if (!isAwaitingRunStart) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsAwaitingRunStart(false);
    }, AWAITING_RUN_START_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isAwaitingRunStart]);

  const hasPendingApproval = hasWorkspaceAgentPendingApproval({
    pendingElicitations,
  });

  usePrivateWorkspaceAgentStallMessageRefresh({
    hasPendingApproval,
    isCancelling,
    // `inputRequested` is not "running" for the loader, but after Approve the list can stay
    // `inputRequested` until REST catch-up — still worth polling, or the clock never starts.
    isSessionRunning:
      isWorkspaceAgentSessionRunning({
        activeRunId,
        isAwaitingRunStart,
        isSending,
        isSessionProcessing,
      }) || openSessionStatus === 'inputRequested',
    lastMessageActivityKey: getWorkspaceAgentLastMessageActivityKey(messages),
    refreshLiveSessionMessages,
    refreshOpenSession,
    sessionId,
  });

  const applySessionId = useCallback(
    (nextSessionId: string | null, options?: ApplySessionIdOptions) => {
      // Re-selecting the active session — e.g. clicking the active row in My Tasks to come back
      // to chat — must not drop its local pending-elicitation list. The list only seeds from the
      // session GET on a real key change, so clearing it here would blank the consent dock until
      // the user switched away and back.
      if (nextSessionId === getActiveSessionId()) {
        return;
      }

      sessionIdRef.current = nextSessionId;
      setSessionId(nextSessionId);
      cancelLiveSessionCatchUp();
      cancelGenerationRef.current += 1;
      resetWorkspaceAgentCancelRunGuard(cancelRunGuardRef.current);
      clearElicitations();
      resetRunState();
      setError(null);

      if (!options?.preserveAwaitingRunStart) {
        setIsAwaitingRunStart(false);
      }
    },
    [cancelLiveSessionCatchUp, clearElicitations, getActiveSessionId, resetRunState],
  );

  // Assigned during render: elicitations is declared above `applySessionId`, so its error effect can
  // fire before this callback would exist if it were created later with `useCallback`. The ref starts
  // as a noop; without this write, the first GET failure would be dropped.
  applyUnavailableSessionRef.current = (error: unknown) => {
    const hadOpenSession = getActiveSessionId() !== null;

    applySessionId(null);

    // A failed load is not an entry point. A location left by an announcement click would otherwise
    // be reported on the next blank send. Archive still leaves that location in place.
    if (hadOpenSession) {
      useWorkspaceAgentPanelStore.getState().clearPendingThreadCreatedLocation();
    }

    setError(
      isWorkspaceAgentSessionUnresolvableError(error)
        ? WORKSPACE_AGENT_ERRORS.UNAVAILABLE_CHAT
        : WORKSPACE_AGENT_ERRORS.LOAD_CHAT,
    );
  };

  const selectSession = useCallback(
    (nextSessionId: string) => {
      useWorkspaceAgentPanelStore.getState().resetComposerScopeOptOuts();
      // Opening an existing thread is not an entry point. A location left by an announcement click
      // would otherwise be reported on the next blank send after this thread is archived. Archive
      // and New thread do not come through here.
      useWorkspaceAgentPanelStore.getState().clearPendingThreadCreatedLocation();
      applySessionId(nextSessionId);
    },
    [applySessionId],
  );

  const resetSession = useCallback(() => {
    useWorkspaceAgentPanelStore.getState().resetComposerScopeOptOuts();
    applySessionId(null);
  }, [applySessionId]);

  /**
   * Socket may deliver a session id before `start` answers — only safe when this panel has none yet.
   */
  const adoptSessionIfIdle = useCallback(
    (nextSessionId: string) => {
      if (getActiveSessionId() !== null) {
        return;
      }

      // Through `applySessionId` so that a `start` which reported failure but did create the session
      // does not leave its error banner sitting over the conversation being adopted.
      applySessionId(nextSessionId, { preserveAwaitingRunStart: true });

      // The message that started this conversation may already be on screen, standing without a
      // session because `start` has not answered yet. It belongs to the one being adopted.
      adoptOptimisticMessagesSession(nextSessionId);
    },
    [adoptOptimisticMessagesSession, applySessionId, getActiveSessionId],
  );

  const startSession = useCallback(
    async ({ artifacts, clientMessageId, content, metadata }: StartWorkspaceAgentSessionParams) => {
      const { sessionId: startedSessionId } = await startSessionRequest({
        agentType: 'workspace',
        ...(artifacts && artifacts.length > 0 ? { artifacts: [...artifacts] } : {}),
        message: {
          content,
          metadata: { ...metadata, [WORKSPACE_AGENT_CLIENT_MESSAGE_ID_KEY]: clientMessageId },
        },
        workspaceId,
      });

      // A session picked while this was in flight is the newer intent, and the reader is in a thread
      // they went to deliberately. The conversation just opened reaches them through the selector
      // below either way.
      if (getActiveSessionId() === null) {
        sessionIdRef.current = startedSessionId;
        setSessionId(startedSessionId);

        // The message is already on screen, filed against no session because there was none to file
        // it against when it was sent. This is the conversation it opened.
        adoptOptimisticMessagesSession(startedSessionId);
      }

      await refreshSessions();

      return startedSessionId;
    },
    [adoptOptimisticMessagesSession, getActiveSessionId, refreshSessions, startSessionRequest, workspaceId],
  );

  /** Resolves `{ didSend: false }` instead of throwing so the composer can restore text on failure. */
  const sendMessage = useCallback(
    async ({
      artifacts,
      content,
      metadata,
      optimisticPills,
    }: SendWorkspaceAgentMessageParams): Promise<WorkspaceAgentSendMessageResult> => {
      setError(null);

      const activeSessionId = sessionId;

      beginSend(cancelRunGuardRef.current, activeSessionId);

      // Round-tripped through the message metadata so the confirmed message can be matched back to
      // the optimistic row below, which identical content alone cannot do.
      const clientMessageId = uuidv4();
      const optimisticMetadata =
        optimisticPills && optimisticPills.length > 0
          ? { ...metadata, pills: [...(metadata.pills ?? []), ...optimisticPills] }
          : metadata;

      setIsAwaitingRunStart(true);

      // The first message of a conversation has no session to be filed against until `start` answers,
      // and the composer has already cleared by then. Without a row standing in for it from the moment
      // it is sent, the thread shows its zero state as if nothing had been.
      addOptimisticUserMessage({
        clientMessageId,
        content,
        metadata: optimisticMetadata,
        targetSessionId: activeSessionId,
      });

      try {
        if (activeSessionId) {
          await sendMessageRequest({
            agentType: 'workspace',
            ...(artifacts && artifacts.length > 0 ? { artifacts: [...artifacts] } : {}),
            content,
            metadata: { ...metadata, [WORKSPACE_AGENT_CLIENT_MESSAGE_ID_KEY]: clientMessageId },
            sessionId: activeSessionId,
            workspaceId,
          });

          return sentWorkspaceAgentMessageResult(activeSessionId);
        }

        // `start` carries the first message itself, so it must not also be sent above.
        const startedSessionId = await startSession({ artifacts, clientMessageId, content, metadata });

        return sentWorkspaceAgentMessageResult(startedSessionId);
      } catch (error_) {
        setIsAwaitingRunStart(false);

        // Nothing will ever confirm this row, so it would otherwise sit pending for the rest of the
        // session. A no-op if a `start` that reported failure did create the session after all, since
        // the row has been handed to it by then — and the message it describes did reach the server.
        removeOptimisticUserMessage({ clientMessageId, targetSessionId: activeSessionId });

        // The banner belongs to the conversation the message was addressed to. Over one picked since,
        // it would report a failed send in a thread that never sent anything.
        if (getActiveSessionId() === activeSessionId) {
          setError(
            getErrorMessage(
              error_,
              activeSessionId !== null ? WORKSPACE_AGENT_ERRORS.SEND_MESSAGE : WORKSPACE_AGENT_ERRORS.START_SESSION,
            ),
          );
        }

        return failedWorkspaceAgentSendMessageResult;
      }
    },
    [
      addOptimisticUserMessage,
      getActiveSessionId,
      removeOptimisticUserMessage,
      sendMessageRequest,
      sessionId,
      startSession,
      workspaceId,
    ],
  );

  const applySessionUpdateFromWss = useCallback(
    (event: WorkspaceAgentSessionUpdatedEvent) => {
      const patch = buildWorkspaceAgentSessionListCachePatch(event.updatedAt, event.updates);

      patchPrivateWorkspaceAgentSessionsListCache(
        queryClient,
        buildPrivateWorkspaceAgentSessionsListInfiniteParams(workspaceId),
        event.sessionId,
        patch,
      );
      patchPrivateWorkspaceAgentSessionsListCache(
        queryClient,
        buildPrivateWorkspaceAgentSessionsListArchivedParams(workspaceId),
        event.sessionId,
        patch,
      );
    },
    [queryClient, workspaceId],
  );

  const applySessionArchivedFromWss = useCallback(
    (event: WorkspaceAgentSessionArchivedEvent) => {
      movePrivateWorkspaceAgentSessionToArchivedListCache(
        queryClient,
        workspaceId,
        event.sessionId,
        buildWorkspaceAgentSessionListCachePatch(new Date().toISOString(), { status: 'archived' }),
      );

      if (getActiveSessionId() === event.sessionId) {
        applySessionId(null);
      }
    },
    [applySessionId, getActiveSessionId, queryClient, workspaceId],
  );

  const applySessionUnarchivedFromWss = useCallback(
    (event: WorkspaceAgentSessionUnarchivedEvent) => {
      movePrivateWorkspaceAgentSessionToLiveListCache(
        queryClient,
        workspaceId,
        event.sessionId,
        buildWorkspaceAgentSessionListCachePatch(new Date().toISOString(), { status: 'idle' }),
      );

      const activeSessionId = getActiveSessionId();

      if (activeSessionId === event.sessionId) {
        void queryClient.invalidateQueries({
          queryKey: getPrivateWorkspaceAgentSessionGetKey({ sessionId: event.sessionId, workspaceId }),
        });
      }
    },
    [getActiveSessionId, queryClient, workspaceId],
  );

  const cancelRun = useCallback(async () => {
    if (sessionId === null || cancellingSessionIdRef.current === sessionId || isSendingRef.current) {
      return;
    }

    cancellingSessionIdRef.current = sessionId;
    setCancellingSessionId(sessionId);
    setError(null);
    const cancelGeneration = ++cancelGenerationRef.current;
    const { shouldSuppressRunStarted } = beginCancel(cancelRunGuardRef.current, {
      sessionId,
      activeRunId,
      isAwaitingRunStart,
    });

    try {
      await cancelSessionRequest({
        agentType: 'workspace',
        sessionId,
        workspaceId,
      });

      // WSS may finish and clear the in-flight cancel before this HTTP settles; ignore stale answers.
      if (
        getActiveSessionId() === sessionId &&
        cancellingSessionIdRef.current === sessionId &&
        cancelGeneration === cancelGenerationRef.current
      ) {
        setIsAwaitingRunStart(false);
        handleCancelSucceeded();
        cancelSucceeded(cancelRunGuardRef.current, shouldSuppressRunStarted);

        try {
          await refreshSessions();
        } catch {
          // Cancel already succeeded; a stale sessions list refetch must not surface as a stop failure.
        }
      }
    } catch (error_) {
      if (
        getActiveSessionId() === sessionId &&
        cancellingSessionIdRef.current === sessionId &&
        cancelGeneration === cancelGenerationRef.current
      ) {
        setError(getErrorMessage(error_, WORKSPACE_AGENT_ERRORS.CANCEL_RUN));

        if (shouldSuppressRunStarted) {
          const { replayRunStarted, shouldClearAwaitingRunStart } = cancelFailed(cancelRunGuardRef.current);

          if (replayRunStarted !== null) {
            onRunStarted(replayRunStarted);
          } else if (shouldClearAwaitingRunStart) {
            setIsAwaitingRunStart(false);
          }
        }

        cancellingSessionIdRef.current = null;
        setCancellingSessionId((current) => (current === sessionId ? null : current));
      }
    }
  }, [
    activeRunId,
    cancelSessionRequest,
    getActiveSessionId,
    handleCancelSucceeded,
    isAwaitingRunStart,
    onRunStarted,
    refreshSessions,
    sessionId,
    workspaceId,
  ]);

  // `messages` is kept off this object: it changes per streamed token and would re-render every
  // consumer of the session context.
  const api = useMemo(
    () => ({
      getActiveSessionId,
      getMessagesSnapshot,
      getRunsById,
      sessionId,
      sessions,
      pendingElicitations,
      getConsentRememberForSession,
      setConsentRememberForSession,
      error,
      isSending,
      isCancelling,
      isComposerRunLocked,
      isLoadingSessions: sessionsQuery.isLoading || archivedSessionsQuery.isLoading,
      isLoadingMessages: messagesQuery.isLoading,
      isSessionProcessing,
      isSessionAwaitingInput,
      elicitationFollowUpAssistantMessageIds,
      elicitationFollowUpSawSessionActivity,
      isAwaitingElicitationFollowUp,
      isAwaitingRunStart,
      activeRunId,
      runsById,
      applySessionArchivedFromWss,
      applySessionUnarchivedFromWss,
      applySessionUpdateFromWss,
      cancelRun,
      handleRunFinished: onRunFinished,
      handleRunStarted: onRunStarted,
      linkActiveRunToUserMessage,
      resetRunState,
      sendMessage,
      selectSession,
      resetSession,
      adoptSessionIfIdle,
      respondToElicitation,
      clearElicitationFollowUp,
      noteElicitationFollowUpSawSessionActivity,
      refreshSessions,
      fetchNextLiveSessionsPage,
      fetchNextArchivedSessionsPage,
      hasNextLiveSessionsPage,
      hasNextArchivedSessionsPage,
      hasNextSessionsPage,
      isFetchingNextLiveSessionsPage,
      isFetchingNextArchivedSessionsPage,
      isFetchingNextSessionsPage,
      fetchNextMessagesPage,
      hasNextMessagesPage: !!hasNextMessagesPage,
      isFetchingNextMessagesPage,
      upsertMessage,
      appendParsedElicitation,
      removeElicitation,
      workspaceId,
    }),
    [
      getActiveSessionId,
      getMessagesSnapshot,
      getRunsById,
      sessionId,
      sessions,
      pendingElicitations,
      getConsentRememberForSession,
      setConsentRememberForSession,
      error,
      isSending,
      sessionsQuery.isLoading,
      archivedSessionsQuery.isLoading,
      messagesQuery.isLoading,
      isSessionProcessing,
      isSessionAwaitingInput,
      elicitationFollowUpAssistantMessageIds,
      elicitationFollowUpSawSessionActivity,
      isAwaitingElicitationFollowUp,
      isAwaitingRunStart,
      activeRunId,
      runsById,
      isCancelling,
      isComposerRunLocked,
      onRunFinished,
      onRunStarted,
      linkActiveRunToUserMessage,
      resetRunState,
      applySessionArchivedFromWss,
      applySessionUnarchivedFromWss,
      applySessionUpdateFromWss,
      cancelRun,
      sendMessage,
      selectSession,
      resetSession,
      adoptSessionIfIdle,
      respondToElicitation,
      clearElicitationFollowUp,
      noteElicitationFollowUpSawSessionActivity,
      refreshSessions,
      fetchNextLiveSessionsPage,
      fetchNextArchivedSessionsPage,
      hasNextLiveSessionsPage,
      hasNextArchivedSessionsPage,
      hasNextSessionsPage,
      isFetchingNextLiveSessionsPage,
      isFetchingNextArchivedSessionsPage,
      isFetchingNextSessionsPage,
      fetchNextMessagesPage,
      hasNextMessagesPage,
      isFetchingNextMessagesPage,
      upsertMessage,
      appendParsedElicitation,
      removeElicitation,
      workspaceId,
    ],
  );

  return useMemo(() => ({ api, messages, scheduleLiveSessionCatchUp }), [api, messages, scheduleLiveSessionCatchUp]);
};

/** Panel session context — live catch-up stays on the provider/WSS tree, not this contract. */
export type PrivateWorkspaceAgentSessionApi = ReturnType<typeof usePrivateWorkspaceAgentSession>['api'];
